import api from '../api'
import { showWhitelistErrorModal } from './modals'
import { shouldPresentMediaService } from '../utils/mediaDeliveryUrls'

// http://plickers-api-documentation-pgjd3e8jb7.s3-website-us-east-1.amazonaws.com/#cloudinary

// Extracts the backend error string from a media-service 400 error.
// Two shapes depending on the upload path:
//   - fetch-based (sign/complete): error.response is a native Response, body consumed
//     as text → error.response.textString contains the raw JSON string.
//   - superagent (small-file direct upload): error.response.body is already parsed JSON.
// NOTE: Do NOT use error.response.body for fetch errors — it is a ReadableStream (truthy)
// and will shadow the textString check.
function getUploadErrorMessage(error) {
  try {
    if (!error.response) return null
    // fetch path: textString was captured by checkStatus in api.js
    if (error.response.textString) {
      const parsed = JSON.parse(error.response.textString)
      return (parsed && typeof parsed.error === 'string') ? parsed.error : null
    }
    // superagent path: body is a plain parsed object
    if (error.response.body && typeof error.response.body.error === 'string') {
      return error.response.body.error
    }
  } catch (e) { /* ignore JSON parse errors */ }
  return null
}

function isUnsupportedFileTypeError(error) {
  const msg = getUploadErrorMessage(error)
  return msg !== null && msg.includes('File validation failed')
}

function isFileTooLargeError(error) {
  const msg = getUploadErrorMessage(error)
  return msg !== null && msg.includes('File size exceeds')
}

function debug(...args) {
  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(...args)
  }
}

// TODO IMPORTANT !!! - setting this to true will use media service response for uploads
// in prod this should be enabled after full migration to media service as a final/final step
// originally intended for development testing
const USE_MEDIASERVICE_RESPONSE = true

// Files at or above this size use the S3 direct upload path (sign → PUT → complete)
// to bypass API Gateway / Lambda POST body size limits.
const LARGE_FILE_THRESHOLD = 1_048_576 // 1 MB

// Convert a base64 data URL to a Blob for raw S3 PUT
function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(',')
  const mime = header.match(/:(.*?);/)[1]
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}

// Returns approximate byte size for both File/Blob objects and base64 data URLs
function getFileSize(fileOrDataUrl) {
  if (typeof fileOrDataUrl === 'string') {
    const b64 = fileOrDataUrl.split(',')[1] || ''
    return Math.floor((b64.length * 3) / 4)
  }
  return fileOrDataUrl.size || 0
}

export function uploadNewImage(file) {
  return (dispatch) => api.fetch('/mediaservice?type=image')
    .then((response) => dispatch(uploadToMediaService(
      file,
      response.timestamp,
      response.signature,
      response.public_id,
      response.upload_preset,
    ))
      .then((response) => response)
      .catch((error) => {
        const errorMessage = getUploadErrorMessage(error)
        if (error.response && error.response.status === 400 && isUnsupportedFileTypeError(error)) {
          dispatch(showWhitelistErrorModal('unsupportedImageType', errorMessage))
        } else if (error.response && error.response.status === 400 && isFileTooLargeError(error)) {
          dispatch(showWhitelistErrorModal('fileTooLarge', errorMessage))
        } else if (error.response && (
          error.response.status === 400 ||
          error.response.status === 401 ||
          error.response.status === 404
        )) {
          // re-throw 4xx errors for the caller (beginImageUpload) to handle
        } else {
          dispatch(showWhitelistErrorModal('mediaUploadFailed'))
        }
        throw error
      }))
}

export function uploadProfileImage(image) {
  return (dispatch) => api.fetch('/mediaservice?type=profile')
    .then((response) => dispatch(uploadToMediaService(
      image,
      response.timestamp,
      response.signature,
      response.public_id,
      response.upload_preset,
    ))
      .then((response) => response)
      .catch((error) => {
        const errorMessage = getUploadErrorMessage(error)
        if (error.response && error.response.status === 400 && isUnsupportedFileTypeError(error)) {
          dispatch(showWhitelistErrorModal('unsupportedImageType', errorMessage))
        } else if (error.response && error.response.status === 400 && isFileTooLargeError(error)) {
          dispatch(showWhitelistErrorModal('fileTooLarge', errorMessage))
        } else {
          dispatch(showWhitelistErrorModal('mediaUploadFailed'))
        }
        throw error
      }))
}

export function uploadRepoImage(image, repoId) {
  return (dispatch) => api.fetch(`/mediaservice?type=repo&id=${repoId}`)
    .then((response) => dispatch(uploadToMediaService(
      image,
      response.timestamp,
      response.signature,
      response.public_id,
      response.upload_preset,
    ))
      .then((response) => response)
      .catch((error) => {
        const errorMessage = getUploadErrorMessage(error)
        if (error.response && error.response.status === 400 && isUnsupportedFileTypeError(error)) {
          dispatch(showWhitelistErrorModal('unsupportedImageType', errorMessage))
        } else if (error.response && error.response.status === 400 && isFileTooLargeError(error)) {
          dispatch(showWhitelistErrorModal('fileTooLarge', errorMessage))
        } else {
          dispatch(showWhitelistErrorModal('mediaUploadFailed'))
        }
        throw error
      }))
}

// used for showing progress bar during sound upload
export function uploadSoundToCloudinaryWithProgress(file, onProgress) {
  return (dispatch) => {
    const shouldUseMediaService = shouldPresentMediaService({ isSound: true })

    const cloudinaryPromise = api.fetch('/cloudinary?type=sound')
      .then((response) => dispatch(uploadToCloudinaryWithProgress(
        file,
        // Only use cloudinary progress if not using mediaservice response
        shouldUseMediaService && USE_MEDIASERVICE_RESPONSE ? () => {} : onProgress,
        response.api_key,
        response.timestamp,
        response.signature,
        response.public_id,
        response.upload_preset,
      ))
        .then((uploadResponse) => uploadResponse))

    if (shouldUseMediaService) {
      const mediaServicePromise = api.fetch('/mediaservice?type=sound')
        .then((response) => dispatch(uploadToMediaServiceWithProgress(
          file,
          // Use mediaservice progress if USE_MEDIASERVICE_RESPONSE is true
          USE_MEDIASERVICE_RESPONSE ? onProgress : () => {},
          response.timestamp,
          response.signature,
          response.public_id,
          response.upload_preset,
        ))
          .then((uploadResponse) => uploadResponse))

      const mediaServicePromiseWithErrorHandling = mediaServicePromise.catch((error) => {
        const errorMessage = getUploadErrorMessage(error)
        if (isUnsupportedFileTypeError(error)) {
          dispatch(showWhitelistErrorModal('unsupportedSoundType', errorMessage))
        } else if (isFileTooLargeError(error)) {
          dispatch(showWhitelistErrorModal('fileTooLarge', errorMessage))
        } else {
          dispatch(showWhitelistErrorModal('mediaUploadFailed'))
        }
        throw error
      })

      return Promise.all([
        cloudinaryPromise.catch(() => null),
        mediaServicePromiseWithErrorHandling,
      ])
        .then(([cloudinaryResponse, mediaServiceResponse]) => {
          if (USE_MEDIASERVICE_RESPONSE && mediaServiceResponse) {
            debug('using media service response', mediaServiceResponse)
            return mediaServiceResponse
          }
          debug('using cloudinary response', cloudinaryResponse)
          return cloudinaryResponse
        })
    }
    debug('using cloudinary response', cloudinaryPromise)
    return cloudinaryPromise
  }
}

export function uploadToCloudinaryWithProgress(
  file,
  onProgress,
  apiKey,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  return () => api.postCloudinaryWithProgress(
    file,
    onProgress,
    apiKey,
    timestamp,
    signature,
    publicId,
    uploadPreset,
  )
    .then((uploadResponse) => uploadResponse)
}

function uploadToMediaServiceWithProgress(
  file,
  onProgress,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  return async () => {
    if ((file instanceof Blob ? file.size : 0) >= LARGE_FILE_THRESHOLD) {
      onProgress({ percent: 1 })
      const { presignedUrl, versionId } = await api.postMediaServiceSignUpload(
        timestamp, signature, publicId, uploadPreset,
      )
      onProgress({ percent: 5 })
      await api.putS3PresignedWithProgress(presignedUrl, file, (pct) => {
        onProgress({ percent: 5 + Math.round(pct * 0.9) }) // maps 0–100 → 5–95
      })
      onProgress({ percent: 95 })
      const response = await api.postMediaServiceCompleteUpload(
        timestamp, signature, publicId, uploadPreset, versionId,
      )
      onProgress({ percent: 100 })
      return response
    }
    return api.postMediaServiceWithProgress(
      file,
      onProgress,
      timestamp,
      signature,
      publicId,
      uploadPreset,
    )
  }
}

function uploadToMediaService(
  file,
  timestamp,
  signature,
  publicId,
  uploadPreset,
) {
  return async () => {
    if (getFileSize(file) >= LARGE_FILE_THRESHOLD) {
      const blob = typeof file === 'string' ? dataUrlToBlob(file) : file
      const { presignedUrl, versionId } = await api.postMediaServiceSignUpload(
        timestamp, signature, publicId, uploadPreset,
      )
      await api.putS3Presigned(presignedUrl, blob)
      return api.postMediaServiceCompleteUpload(
        timestamp, signature, publicId, uploadPreset, versionId,
      )
    }
    const form = new FormData()
    form.append('file', file)
    form.append('timestamp', timestamp)
    form.append('signature', signature)
    form.append('public_id', publicId)
    if (uploadPreset) {
      form.append('upload_preset', uploadPreset)
    }
    return api.postMediaService(form)
  }
}
